import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import StartTesting, { selectionsFor } from "./StartTesting";
import { atLeast, ApiError, api, ago, on, type Bench, type Campaign, type Job } from "../api";
import { Notice } from "./Look";

export { selectionsFor } from "./StartTesting";

/**
 * Start testing on a bench, and show what is queued there.
 *
 * The form itself is `StartTesting`, the same one the project page and Now
 * open (clear-screens stop B). What stays here belongs to THIS bench: the job
 * on the rig now, what waits behind it, and what was asked for before. A run
 * takes hours and there is one rig, so the cost of a mistaken click is
 * somebody else's afternoon - which is why the queue sits right above the form.
 */
export default function RunPanel({ bench }: { bench: Bench }) {
  const { t } = useTranslation();
  const mayOperate = atLeast(bench.role, "operator");
  const [jobs, setJobs] = useState<Job[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    try {
      setJobs((await api.jobs(bench.id)).jobs);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
    try {
      setCampaigns((await api.campaigns({ bench: bench.id, limit: 3 })).campaigns.slice(0, 3));
    } catch {
      /* a service from before campaigns simply has none to list */
    }
  }, [bench.id]);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 10000);
    return () => clearInterval(t);
  }, [load]);

  const profiles = (bench.agent_caps ?? []).includes("profiles") ? (bench.profiles ?? []) : [];
  const profileName = (id?: string) =>
    profiles.find((p) => p.id === id)?.label ?? id ?? "";
  // A job as people read it: the profile or the test group by name, then what
  // narrowed it and which build - not "-m auto -k LED".
  const what = (j: { execution_profile?: string; marker: string; k_filter?: string; build_ref?: string }) => {
    const group = selectionsFor(bench).find((x) => x.marker === j.marker);
    return [
      j.execution_profile
        ? profileName(j.execution_profile)
        : group ? (group.key ? t(group.key) : group.label) : t("runPanel.jobMarked", { marker: j.marker }),
      j.k_filter ? t("runPanel.jobMatching", { filter: j.k_filter }) : "",
      j.build_ref ? t("runPanel.jobBuild", { build: j.build_ref }) : "",
    ].filter(Boolean).join(" · ");
  };

  const pending = jobs.filter((j) =>
    ["queued", "claimed", "running"].includes(j.state),
  );
  const live = pending.find((j) => ["claimed", "running"].includes(j.state));
  // Next to run first. The list arrives newest first; a campaign queued as one
  // read backwards that way, its last stop on top.
  const waiting = pending
    .filter((j) => j.state === "queued")
    .sort((a, b) => a.created_at - b.created_at || a.id - b.id);

  return (
    <section className="mt-6">
      <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">
        {t("runPanel.heading")}
      </h2>

      {note && (
        <p className="mt-2 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-600 dark:border-slate-800 dark:text-slate-300">
          {note}
        </p>
      )}

      {error && <Notice tone="error">{error}</Notice>}

      {live ? (
        <div className="mt-2 rounded-lg border border-sky-300 bg-sky-50 px-4 py-3 text-sm dark:border-sky-900 dark:bg-sky-950/40">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-sky-900 dark:text-sky-200">
              {live.state === "running" ? t("runPanel.running") : t("runPanel.queued")}
              {live.run_name ? ` — ${live.run_name}` : ""}
            </span>
            <span className="text-sky-900/70 dark:text-sky-200/70">
              {what(live)} ·{" "}
              {t("runPanel.askedBy", {
                who: live.requested_by,
                when: ago(live.created_at),
              })}
            </span>
            <button
              className="ml-auto rounded border border-sky-400 px-2 py-1 text-xs text-sky-900 hover:bg-sky-100 disabled:opacity-40 dark:border-sky-800 dark:text-sky-200"
              disabled={!mayOperate}
              title={mayOperate ? "" : t("runPanel.stopTitle")}
              onClick={async () => {
                try {
                  const r = await api.cancelJob(live.id);
                  // "Stopped" and "will stop shortly" are different promises.
                  setNote(
                    r.stopping ? t("runPanel.stopping") : t("runPanel.removed"),
                  );
                  await load();
                } catch (e) {
                  setError(e instanceof ApiError ? e.message : String(e));
                }
              }}
            >
              {t("runPanel.stop")}
            </button>
          </div>
          {live.reason && (
            <p className="mt-1 text-sky-900/80 dark:text-sky-200/80">{live.reason}</p>
          )}
          {live.state === "running" && (
            <p className="mt-1 text-xs text-sky-900/60 dark:text-sky-200/60">
              {t("runPanel.cancelNote")}
            </p>
          )}
        </div>
      ) : null}

      {waiting.length > 0 && (
        <div className="mt-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="text-xs font-medium text-slate-500">
            {t("runPanel.waiting", { count: waiting.length })}
          </div>
          <ul className="mt-1 space-y-0.5">
            {waiting.map((j) => (
              <li key={j.id} className="flex items-baseline gap-2 text-xs">
                <span>{what(j)}</span>
                <span className="text-slate-500">{j.requested_by}</span>
                <button
                  className="ml-auto text-slate-400 underline hover:text-slate-600"
                  onClick={async () => {
                    try {
                      await api.cancelJob(j.id);
                      setNote(t("runPanel.removed"));
                      await load();
                    } catch (e) {
                      setError(e instanceof ApiError ? e.message : String(e));
                    }
                  }}
                >
                  {t("runPanel.remove")}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {campaigns.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-sm" aria-label={t("campaign.recent")}>
          {campaigns.map((c) => (
            <li key={c.id}>
              <Link className="underline" to={`/campaigns/${c.id}`}>
                {t("campaign.listItem", { when: ago(c.created_at), ran: c.ran, planned: c.planned ?? "?" })}
              </Link>
              <span className="text-slate-500"> · {t(`campaign.status.${c.status}`)}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-2">
        <StartTesting benches={[bench]} benchId={bench.id} confirm={false} onQueued={load} />
      </div>

      {jobs.length > 0 && (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs text-slate-500">
            {t("runPanel.earlier", { count: jobs.length })}
          </summary>
          <ul className="mt-1 space-y-0.5 text-xs text-slate-500">
            {jobs.map((j) => (
              <li key={j.id}>
                <span>{on(j.created_at)}</span> ·{" "}
                <b>{t(`runPanel.jobState.${j.state}`)}</b> ·{" "}
                {what(j)}
                {/* Which build it was queued against, when one was named. A
                    queue that shows only the marker cannot answer "what is
                    this actually testing". */}
                {" · "}{j.requested_by}
                {j.run_name ? ` · ${j.run_name}` : ""}
                {j.detail ? ` — ${j.detail}` : ""}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
